import type {
  DocumentData,
  DocumentSnapshot,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannelMessage } from '@ps/models/lib/community-channel';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';

/**
 * Coerce a Firestore message doc into the unified
 * {@link ExtendedCommunityChannelMessage} (canonical doc + `id`/`channelId`).
 * Shared between the channel-messages subscription and the thread-replies
 * subscription (same collection, different `parentMessageId`/`level` filters).
 *
 * The document is WRITTEN server-side in the exact canonical
 * `CommunityChannelMessage` shape — native `Timestamp`s, object
 * `content`/`links`/`attachments`, model `reactions` — so
 * {@link snapshotToDataWithId} attaches the doc `id` typed as the source-of-
 * truth model (`FirestoreTypes.ts` maps `communityChannels/messages` → it) and
 * we add the render-only `channelId`. No field-by-field duck typing.
 */
export function firestoreDocToMessage(
  channelId: string,
  doc: DocumentSnapshot<DocumentData>,
): ExtendedCommunityChannelMessage | undefined {
  const base = snapshotToDataWithId<CommunityChannelMessage>(doc);
  return base ? { ...base, channelId } : undefined;
}
